import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  BookingBookmarkModel,
  CreateProfileRequestAccessEnum,
  ProfileResponse,
  ShipmentBookmarkModel,
} from '@freighthub/user-profile-core-client'

import { useBookingBookmarkCreate, useBookingBookmarkDelete, useGetBookingBookmarks } from '@/features/bookings'

import { useGetShipmentBookmarks } from '../../shipments/api/useGetShipmentBookmarks'
import { useShipmentBookmarkCreate } from '../../shipments/api/useShipmentBookmarkCreate'
import { useShipmentBookmarkDelete } from '../../shipments/api/useShipmentBookmarkDelete'

export type UseBookmark = {
  createShipmentBookmark: (shipmentId: string) => Promise<void>
  bookmarkedShipmentIds: string[]
  deleteShipmentBookmark: (shipmentId: string) => Promise<void>
  createBookingBookmark: (bookingId: string) => Promise<void>
  bookmarkedBookingIds: string[]
  deleteBookingBookmark: (bookingId: string) => Promise<void>
  bookmarkedEntityIds: string[]
  isLoadingBookmarks: boolean
}

const BookmarkContext = createContext<UseBookmark | null>(null)

export const BookmarkContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [shipmentBookmarks, setShipmentBookmarks] = useState<ProfileResponse[]>([])
  const [bookingBookmarks, setBookingBookmarks] = useState<ProfileResponse[]>([])
  const { data: shipmentBookmarksResponse, isLoading: isShipmentBookmarksLoading } = useGetShipmentBookmarks()
  const { data: bookingBookmarksResponse, isLoading: isBookingBookmarksLoading } = useGetBookingBookmarks()
  const createShipmentBookmarkMutation = useShipmentBookmarkCreate()
  const deleteShipmentBookmarkMutation = useShipmentBookmarkDelete()
  const createBookingBookmarkMutation = useBookingBookmarkCreate()
  const deleteBookingBookmarkMutation = useBookingBookmarkDelete()

  const getBookmarks = useCallback(async (): Promise<void> => {
    setBookingBookmarks(bookingBookmarksResponse ?? [])
    setShipmentBookmarks(shipmentBookmarksResponse ?? [])
  }, [bookingBookmarksResponse, shipmentBookmarksResponse])

  useEffect(() => {
    getBookmarks()
  }, [getBookmarks, isShipmentBookmarksLoading, isBookingBookmarksLoading])

  const state: UseBookmark = useMemo(() => {
    const bookmarkedShipmentIds: string[] =
      shipmentBookmarks?.map((bookmark) => (bookmark.value as ShipmentBookmarkModel).shipmentId) ?? []

    const bookmarkedBookingIds: string[] =
      bookingBookmarks?.map((bookmark) => (bookmark.value as BookingBookmarkModel).bookingId) ?? []

    const bookmarkedEntityIds = bookmarkedBookingIds.concat(bookmarkedShipmentIds)

    const getShipmentBookmarkId = (shipmentId: string): string | undefined => {
      return shipmentBookmarks.find((bookmark) => (bookmark.value as ShipmentBookmarkModel).shipmentId === shipmentId)
        ?.id
    }

    const getBookingBookmarkId = (bookingId: string): string | undefined => {
      return bookingBookmarks.find((bookmark) => (bookmark.value as BookingBookmarkModel).bookingId === bookingId)?.id
    }

    const createShipmentBookmark = async (shipmentId: string) => {
      const bookmark = await createShipmentBookmarkMutation.mutateAsync({
        access: CreateProfileRequestAccessEnum.Owner,
        value: { shipmentId: shipmentId },
      })
      setShipmentBookmarks((prevState) => [...prevState, bookmark])
    }

    const deleteShipmentBookmark = async (shipmentId: string) => {
      const bookmarkId = getShipmentBookmarkId(shipmentId)
      if (bookmarkId) {
        await deleteShipmentBookmarkMutation.mutateAsync(bookmarkId)
        setShipmentBookmarks((prevState) => [...prevState.filter((bookmark) => bookmark.id !== bookmarkId)])
      }
    }

    const createBookingBookmark = async (bookingId: string) => {
      const bookmark = await createBookingBookmarkMutation.mutateAsync({
        access: CreateProfileRequestAccessEnum.Owner,
        value: { bookingId: bookingId },
      })
      setBookingBookmarks((prevState) => [...prevState, bookmark])
    }

    const deleteBookingBookmark = async (bookingId: string) => {
      const bookmarkId = getBookingBookmarkId(bookingId)
      if (bookmarkId) {
        await deleteBookingBookmarkMutation.mutateAsync(bookmarkId)
        setBookingBookmarks((prevState) => [...prevState.filter((bookmark) => bookmark.id !== bookmarkId)])
      }
    }

    return {
      createShipmentBookmark,
      bookmarkedShipmentIds,
      deleteShipmentBookmark,
      createBookingBookmark,
      bookmarkedBookingIds,
      deleteBookingBookmark,
      bookmarkedEntityIds,
      isLoadingBookmarks: isShipmentBookmarksLoading || isBookingBookmarksLoading,
    }
  }, [
    shipmentBookmarks,
    bookingBookmarks,
    isShipmentBookmarksLoading,
    isBookingBookmarksLoading,
    createShipmentBookmarkMutation,
    deleteShipmentBookmarkMutation,
    createBookingBookmarkMutation,
    deleteBookingBookmarkMutation,
  ])

  return <BookmarkContext.Provider value={state}>{children}</BookmarkContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useBookmark = (): UseBookmark => {
  const context = useContext(BookmarkContext)

  if (!context) {
    throw new Error('Must use useBookmark in BookmarkContext')
  }

  return context
}
